import React, { Dispatch, FC, PropsWithChildren, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { STATIC_URL } from "App.constants";
import { delay, uniqBy } from "lodash";
import { Button, Flex, Image, Segmented, Space } from "antd";
import { LessonType, MarkerType, SentenceType, StatusType, StoryType, TaskType } from "App.types";
import { encodeS3URI, isImageUrl } from "App.helpers";
import StoryMaskedDragDrop from "./StoryMaskedDragDrop";
import styles from "./StoryTask.module.scss";
import Dictionary from "Components/Dictionary";
import StorySentenceDragDrop from "./StorySentenceDragDrop";
import StorySentence from "Components/StorySentence";
import StoryDialogSentence from "Components/StoryDialogSentence";
import { Element, scroller } from "react-scroll";
import VideoStoryTask from "../VideoTask";
import StoryMaskedTask from "Pages/Books/Page/StoryMaskedTask";
import { CheckOutlined, PauseOutlined, SoundFilled, ZoomInOutlined } from "@ant-design/icons";
import cx from "classnames";
import StoryMatchTask from "./StoryMatchTask";
import { isMobile } from "react-device-detect";
import TaskPanelBasic from "Components/TaskPanelBasic";
import SentenceTaskHelpButton from "../SentenceTaskHelpButton";
import ReportFeedback from "Components/ReportFeedback";
import useTemplatedSentences from "../useTemplatedSentences";
import useSentencesAudios from "Hooks/useSentencesAudios";
import { useIsBeginner } from "Hooks/useIsBeginner";
import { useSpeechCheckRead } from "Hooks/useSpeechCheckRead";
import RecordButton from "Components/RecordButton";
import useSocketSpeech from "Hooks/useSocketSpeech";

const StoryTask: FC<
  PropsWithChildren<{
    story: StoryType;
    mediaStory: StoryType | undefined;
    lesson: LessonType;
    activeType?: TaskType;
    onTaskComplete: () => any;
    onNext: () => any;
    media?: string;
    autoPlay?: boolean;
    markers: MarkerType[];
    showGrammar?: Dispatch<SetStateAction<boolean>>;
  }>
> = ({
  mediaStory,
  markers = [],
  autoPlay = true,
  media,
  story,
  lesson,
  onTaskComplete,
  activeType = TaskType.StoryListen,
  onNext,
  showGrammar,
}) => {
  const [status, setStatus] = useState<StatusType>(StatusType.Empty);
  const [source, setSource] = useState<string>("");
  const [activeSentIdx, setActiveSentIdx] = useState<number>(autoPlay ? -1 : story.sentences?.length);
  const [activeWordIdx, setActiveWordIdx] = useState<number>(-1);
  const [audioStatus, setAudioStatus] = useState<StatusType>(StatusType.Empty);
  const [isDictOpened, setDictOpened] = useState(false);

  const { sentences: initialSentences, id: storyId } = story;

  const sentences = useTemplatedSentences(initialSentences);

  const isBeginner = useIsBeginner();

  const uniqSentences = useMemo(
    () =>
      uniqBy(
        sentences.filter((s) => s.text),
        "text",
      ),
    [sentences],
  );

  const sentence = useMemo<SentenceType | undefined>(() => sentences[activeSentIdx], [sentences, activeSentIdx]);

  const onCheckRef = useRef<{ onCheck: () => any }>(null);

  const {
    start,
    stop,
    reset,
    results: speechResults,
    isSpeaking,
  } = useSocketSpeech({ setStatus, text: sentence?.text, tags: sentences[activeSentIdx]?.tags });

  const { completedTagIdx, reset: resetSpeechCheck } = useSpeechCheckRead({ sentence, speechResults });

  useEffect(() => {
    if (activeSentIdx >= 0 && activeSentIdx < sentences.length) {
      scroller.scrollTo(`${activeSentIdx}`, { smooth: true, isDynamic: true, offset: -100 });
    }
  }, [activeSentIdx, sentences.length, story.type]);

  useEffect(() => {
    if (sentence && completedTagIdx >= sentence.tags.length - 1) {
      if (activeSentIdx >= sentences.length - 1) {
        stop();
        setStatus(StatusType.Completed);
        onTaskComplete();
      } else {
        delay(() => {
          setActiveSentIdx((prev) => prev + 1);
          reset();
          resetSpeechCheck();
        }, 600);
      }
    }
  }, [resetSpeechCheck, onTaskComplete, sentence, stop, completedTagIdx, activeSentIdx, reset, sentences]);

  const onClickRecording = () => {
    if ([StatusType.IsRecording, StatusType.Loading].includes(status)) {
      stop();
      setActiveWordIdx(-1);
    } else {
      setActiveSentIdx((prevState) => (prevState > 0 ? prevState : 0));
      resetSpeechCheck();
      start();
    }
  };

  useEffect(() => {
    // setActiveSentIdx(activeType === "listen" ? Infinity : 0);
    setStatus(StatusType.Empty);
  }, [activeType]);

  const audios = useSentencesAudios({ sentences: activeType === TaskType.Match ? [] : sentences });

  useEffect(() => {
    if (autoPlay) setActiveSentIdx(0);
  }, [autoPlay]);

  useEffect(() => {
    audios.forEach((aud, idx) => {
      aud.onplay = () => setAudioStatus(StatusType.isPlaying);
      aud.onended = ({ type }) => {
        if (type === "ended") {
          if (sentences[activeSentIdx + 1]?.description === "pause") {
            delay(() => setActiveSentIdx((prev) => prev + 1), 2000);
            if ([TaskType.StoryListen, TaskType.Dictionary].includes(activeType) || markers?.includes("noTranslate")) {
              delay(() => audios[activeSentIdx + 2]?.play(), 2200);
            }
          } else
            setActiveSentIdx((prev) => {
              if (sentences.length <= prev + 1 && [TaskType.StoryListen, TaskType.Dictionary].includes(activeType)) {
                onTaskComplete();
                setStatus(StatusType.Completed);
              }
              setAudioStatus(StatusType.Empty);
              const next = prev + (sentences[idx + 1]?.text ? 1 : 2); // 2 - если есть пустая строка между предложениями
              if ([TaskType.StoryListen, TaskType.Dictionary].includes(activeType) || markers?.includes("noTranslate")) {
                delay(() => audios[next]?.play(), 800);
              }
              return next;
            });
        }
      };
    });
  }, [audios, autoPlay, activeType, onTaskComplete, sentences, markers, activeSentIdx]);

  const activeAudio = useMemo(() => audios?.[activeSentIdx], [audios, activeSentIdx]);

  useEffect(() => {
    if (activeSentIdx === -1) {
      setActiveSentIdx(0);
    }
  }, [activeSentIdx, activeAudio]);

  const onComplete = useCallback(() => {
    setStatus(StatusType.Completed);
    onTaskComplete();
  }, [onTaskComplete]);

  const renderImage = () => (
    <Image
      wrapperClassName={cx(styles.image)}
      className={styles.image__img}
      onError={(e: any) => (e.target.style.visibility = "hidden")}
      src={`${STATIC_URL}/media/${media}`}
      // style={{ backgroundImage: `url(${STATIC_URL}/media/${activeSentence.media})` }}
      alt={media}
      preview={{
        toolbarRender: () => null,
        mask: <ZoomInOutlined style={{ fontSize: 24 }} />,
        maskClassName: styles.mask,
      }}
    />
  );

  return (
    <div className={cx(styles.storyTask, { [styles.storyTask_mobile]: isMobile })}>
      {!isMobile && isImageUrl(media) && activeType !== TaskType.MaskedDragDrop && renderImage()}

      {markers.includes("onlyAudio") && mediaStory && (
        <audio
          style={{ marginBottom: 10 }}
          controls
          src={`${STATIC_URL}/stories/${encodeS3URI(mediaStory.title)}/${mediaStory.audio || "audio.mp3"}`}
        />
      )}

      {media?.includes(".mp3") && (
        <div>
          <audio style={{ marginBottom: 10 }} src={`${STATIC_URL}/media/${media}`} controls />
        </div>
      )}

      {[TaskType.StoryRead, TaskType.StoryListen].includes(activeType) && story.type !== "video" && (
        <Segmented className={styles.segmented} onChange={(v) => setSource(v as string)} options={["Оригинал", "Перевод", "Все вместе"]} />
      )}

      <div className={styles.storyTask__text}>
        {story.type === "dialog" && [TaskType.StoryListen].includes(activeType) && (
          <Flex vertical style={{ maxWidth: 680 }} gap={10}>
            {sentences.slice(0, activeSentIdx + 1).map((sentence, idx) => (
              <Element name={`${idx}`} key={sentence.id}>
                {!!sentence.text && (
                  <Flex justify={sentence.isLeft ? "flex-start" : "flex-end"}>
                    <div style={{ maxWidth: "90%" }}>
                      <StoryDialogSentence
                        storyId={storyId}
                        active={idx === activeSentIdx}
                        activeWordIdx={idx === activeSentIdx ? activeWordIdx : undefined}
                        audio={audios?.[idx]}
                        sentence={sentence}
                        showTranslate={source === "Перевод"}
                        withTranslate={source === "Все вместе"}
                        showText={["Оригинал", "Перевод"].includes(source)}
                      />
                    </div>
                  </Flex>
                )}
              </Element>
            ))}
          </Flex>
        )}

        {activeType === TaskType.Video && (
          <VideoStoryTask status={status} sentences={sentences} onTaskComplete={onComplete} story={story} autoPlay={autoPlay} />
        )}

        {/*{[TaskType.Dictionary].includes(activeType) &&*/}
        {/*  sentences.map((sentence, idx) => (*/}
        {/*    <Fragment key={sentence.id}>*/}
        {/*      <StorySentence storyId={storyId} active={idx === activeSentIdx} sentence={sentence} audio={audios?.[idx]} />*/}
        {/*      <div />*/}
        {/*    </Fragment>*/}
        {/*  ))}*/}

        {(activeType === TaskType.StoryRead ||
          ([TaskType.StoryListen, TaskType.Dictionary].includes(activeType) && !["dialog"].includes(story.type))) &&
          sentences.map((sentence, idx) => (
            <Element name={`${idx}`} key={sentence.id}>
              <blockquote className={styles.blockquote}>
                <StorySentence
                  storyId={storyId}
                  active={idx === activeSentIdx}
                  activeWordIdx={idx === activeSentIdx ? activeWordIdx : undefined}
                  completedWordIdx={idx === activeSentIdx ? completedTagIdx : undefined}
                  sentence={sentence}
                  marginLeft={false}
                  showTranslate={source === "Перевод"}
                  withTranslate={source === "Все вместе" || TaskType.Dictionary === activeType}
                  showText={["Оригинал", "Перевод"].includes(source)}
                  audio={audios?.[idx]}
                />
                {(story.type === "card" || sentences[idx]?.wrap) && <div />}
              </blockquote>
            </Element>
          ))}
      </div>

      {TaskType.MaskedDragDrop === activeType && (
        <StoryMaskedDragDrop
          story={story}
          isCompleted={status === StatusType.Completed}
          onTaskComplete={onComplete}
          sentences={sentences}
          showTranslate={!markers?.includes("noTranslate")}
        >
          {!isMobile && renderImage()}
        </StoryMaskedDragDrop>
      )}

      {TaskType.StoryMasked === activeType && (
        <StoryMaskedTask
          sentenceId={sentence?.id}
          lessonId={lesson.id}
          showTranslate={!markers?.includes("noTranslate")}
          showTooltip={!markers?.includes("hideMaskedHint")}
          story={story}
          sentences={sentences}
          onNext={onNext}
          onTaskComplete={onComplete}
          showGrammar={showGrammar}
        />
      )}

      {[TaskType.Match].includes(activeType) && (
        <StoryMatchTask isCompleted={status === StatusType.Completed} onTaskComplete={onComplete} sentences={uniqSentences} />
      )}

      {[TaskType.StoryDragDrop].includes(activeType) && (
        <StorySentenceDragDrop
          ref={onCheckRef}
          showTranslate={!markers?.includes("noTranslate")}
          isCompleted={status === StatusType.Completed}
          onTaskComplete={onComplete}
          sentences={uniqSentences}
        />
      )}

      {activeType !== TaskType.StoryMasked && (
        <TaskPanelBasic>
          <ReportFeedback storyId={storyId} task={activeType} lessonId={lesson.id} />

          <Space>
            {(status === StatusType.Completed || ![TaskType.StoryDragDrop].includes(activeType)) && (
              <Button
                disabled={status !== StatusType.Completed}
                icon={<CheckOutlined />}
                className={cx(styles.btn_next)}
                type={"primary"}
                shape={"round"}
                onClick={() => onNext()}
              >
                {isBeginner ? "далее" : "next"}
              </Button>
            )}

            {activeType === TaskType.StoryRead && (
              <RecordButton
                disabled={status === StatusType.Completed}
                onClick={onClickRecording}
                isRecording={status === StatusType.IsRecording}
                loading={status === StatusType.Loading}
                isSpeaking={isSpeaking}
              />
            )}

            {status !== StatusType.Completed && [TaskType.StoryDragDrop].includes(activeType) && (
              <Button type={"primary"} shape={"round"} onClick={() => onCheckRef.current?.onCheck()}>
                {isBeginner ? "проверить" : "check"}
              </Button>
            )}

            {((markers?.includes("noTranslate") && !mediaStory?.audio) ||
              [TaskType.StoryListen, TaskType.Dictionary].includes(activeType)) && (
              <Button
                type={status === StatusType.Completed ? "default" : "primary"}
                icon={audioStatus === StatusType.isPlaying ? <PauseOutlined /> : <SoundFilled />}
                onClick={
                  audioStatus === StatusType.isPlaying
                    ? () => {
                        // pause();
                        activeAudio?.pause();
                        setAudioStatus(StatusType.Empty);
                      }
                    : () => {
                        if (activeSentIdx > sentences.length - 1) {
                          setActiveSentIdx(0);
                        }

                        // if (hasStoryAudio) {
                        //   play();
                        // } else {
                        activeAudio?.play();
                        // }
                      }
                }
              />
            )}
          </Space>
          <SentenceTaskHelpButton storyId={storyId} openDictionary={setDictOpened} showGrammar={showGrammar} />
        </TaskPanelBasic>
      )}

      <Dictionary storyId={storyId} isOpen={isDictOpened} sentences={sentences} toggle={setDictOpened} />
      <div className={styles.placeholder} />
    </div>
  );
};

export default StoryTask;
